import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import {BusinessDetails, FicaDetails, MerchantVerification, MerchantVerificationResponse, WebBusinessService} from 'web-angular-client';

import {AuthenticationService} from '../../sdk/service/authentication.service';
import {KycVerificationSubmission} from './model/kyc-verification-submission';

/**
 * Submits the KYC verification wizard's aggregated data to the backend and remembers the submitted
 * payload for the rest of the session so the read-only "View submission" wizard can pre-fill the
 * client base and average transaction amount, which the FICA / business detail endpoints do not
 * expose. The cached copy is scoped to the merchant that submitted it (keyed by businessId) so it is
 * never read back for another merchant after a login or profile switch, and is cleared on refresh
 * (accepted).
 */
@Injectable({providedIn: 'root'})
export class KycSubmissionService
{
    private sessionSubmission: KycVerificationSubmission | null = null;
    private sessionSubmissionBusinessId: number | null = null;

    constructor(
        private webBusinessService: WebBusinessService,
        private authenticationService: AuthenticationService)
    {
    }

    /**
     * Submits the wizard's data for verification. The wizard captures only the deferred KYC fields,
     * so the already-captured businessName / vatNumber / taxNumber (from the loaded FICA details) and
     * customRefEnabled (from the loaded business details) are carried through to stop the backend's
     * full overwrite from nulling them. The submission is remembered on success for the read-only view.
     *
     * @param submission the aggregated data captured by the four wizard steps
     * @param loadedFicaDetails the merchant's stored FICA details, source of the preserved fields
     * @param loadedBusinessDetails the merchant's stored business details, source of customRefEnabled
     */
    submitKycVerification(
        submission: KycVerificationSubmission,
        loadedFicaDetails: FicaDetails,
        loadedBusinessDetails: BusinessDetails): Observable<MerchantVerificationResponse>
    {
        const merchantVerification: MerchantVerification =
            this.toMerchantVerification(submission, loadedFicaDetails, loadedBusinessDetails);
        return this.webBusinessService.submitMerchantVerification(merchantVerification).pipe(
            tap(() =>
            {
                this.sessionSubmission = submission;
                this.sessionSubmissionBusinessId = this.currentBusinessId();
            }));
    }

    /**
     * The KYC submission captured this session by {@link submitKycVerification}, used to pre-fill the
     * read-only "View submission" wizard. Returns a deep copy so callers cannot mutate the stored
     * submission. Null when nothing was submitted this session (e.g. after a page refresh), or when the
     * cached submission belongs to a different merchant than the one currently logged in.
     */
    getSessionSubmission(): KycVerificationSubmission | null
    {
        if (this.sessionSubmission === null || this.sessionSubmissionBusinessId !== this.currentBusinessId())
        {
            return null;
        }
        return JSON.parse(JSON.stringify(this.sessionSubmission)) as KycVerificationSubmission;
    }

    /**
     * The business id of the currently logged-in merchant, or null when no one is logged in.
     */
    private currentBusinessId(): number | null
    {
        return this.authenticationService.getUserLogin()?.businessId ?? null;
    }

    /**
     * Maps the wizard's aggregated submission onto the MerchantVerification payload, carrying through
     * the already-captured fields the wizard does not collect so the backend's full overwrite does
     * not null them.
     */
    private toMerchantVerification(
        submission: KycVerificationSubmission,
        loadedFicaDetails: FicaDetails,
        loadedBusinessDetails: BusinessDetails): MerchantVerification
    {
        return {
            businessDisplayName: submission.profileDetail?.businessDisplayName,
            clientBase: submission.profileDetail?.clientBase,
            averageTransactionValue: submission.profileDetail?.averageTransactionValue,
            businessType: submission.profileDetail?.businessType,
            registrationNumber: submission.kycDetail?.registrationNumber,
            directorName: submission.kycDetail?.directorName,
            directorSurname: submission.kycDetail?.directorSurname,
            directorIdNumber: submission.kycDetail?.directorIdNumber,
            fspNumber: submission.kycDetail?.fspNumber,
            natureOfBusiness: submission.kycDetail?.natureOfBusiness,
            overview: submission.kycDetail?.overview,
            websiteUrl: submission.kycDetail?.websiteUrl,
            bankName: submission.bankingDetails?.bankName,
            bankBranch: submission.bankingDetails?.bankBranch,
            bankAccountNumber: submission.bankingDetails?.bankAccountNumber,
            address1: submission.addressDetail?.address1,
            address2: submission.addressDetail?.address2,
            city: submission.addressDetail?.city,
            province: submission.addressDetail?.province,
            postalCode: submission.addressDetail?.postalCode,
            billingEmail: submission.addressDetail?.billingEmailAddress,
            businessName: loadedFicaDetails?.businessName,
            vatNumber: loadedFicaDetails?.vatNumber,
            taxNumber: loadedFicaDetails?.taxNumber,
            customRefEnabled: loadedBusinessDetails?.customRefEnabled
        };
    }
}
